/**
 * network-config til boot-partitionen på TV-skærmens SD-kort (beslutning 075).
 *
 * cloud-init læser filen ved allerførste opstart og sætter både kabel og Wi-Fi op.
 * Filen laves i browseren: Wi-Fi-adgangskoden sendes aldrig til serveren.
 *
 * Værdierne skrives som JSON-strenge. JSON er gyldig YAML, så et netværksnavn med
 * kolon, anførselstegn eller # kan ikke ødelægge filen - og en fejl i filen opdages
 * først, når en Pi står foran et TV og ikke kommer på nettet.
 */

export interface WifiSettings {
  ssid: string
  password: string
  hidden?: boolean
  /** Landekode til Wi-Fi-kanaler. Uden den er 5 GHz-kanaler slået fra på Pi'en. */
  country?: string
}

/** Fejlen på dansk, eller null når indstillingerne kan bruges. */
export function validateWifi(ssid: string, password: string): string | null {
  if (!ssid.trim()) {
    return 'Skriv netværkets navn.'
  }
  if (new TextEncoder().encode(ssid).length > 32) {
    return 'Netværkets navn må højst være 32 bytes.'
  }
  if (password && (password.length < 8 || password.length > 63)) {
    return 'En WPA-adgangskode er mellem 8 og 63 tegn.'
  }
  return null
}

export function buildNetworkConfig({ ssid, password, hidden = false, country = 'DK' }: WifiSettings): string {
  const quote = (value: string) => JSON.stringify(value)

  const lines = [
    '# TC Procedurestyringssystem - netværk til TV-skærmen (beslutning 075).',
    '# Lavet under TV-skærme i adminpanelet. Læg filen på SD-kortets boot-partition',
    '# i stedet for den network-config, der ligger der - før skærmen starter første gang.',
    '',
    'network:',
    '  version: 2',
    '  ethernets:',
    '    eth0:',
    '      dhcp4: true',
    '      optional: true',
    '  wifis:',
    '    wlan0:',
    '      dhcp4: true',
    '      optional: true',
    `      regulatory-domain: ${quote(country)}`,
    '      access-points:'
  ]

  const details: string[] = []
  if (password) {
    details.push(`          password: ${quote(password)}`)
  }
  if (hidden) {
    details.push('          hidden: true')
  }

  // Et åbent netværk uden flere oplysninger skal stadig være en tom mapping, ikke null.
  lines.push(details.length > 0 ? `        ${quote(ssid)}:` : `        ${quote(ssid)}: {}`)
  lines.push(...details)

  return `${lines.join('\n')}\n`
}
